import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { MobileNavigation } from '../components/MobileNavigation';
import { ArrowLeft, CheckCircle, XCircle, Clock, CurrencyDollar, User, Calendar, MapPin, Warning, Wallet } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../utils/api';

export function AdminRefunds() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending_count: 0, approved_count: 0, denied_count: 0, pending_amount: 0 });
  const [filter, setFilter] = useState('pending');
  const [processingId, setProcessingId] = useState(null);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [decisionType, setDecisionType] = useState('approve');
  const [adminNotes, setAdminNotes] = useState('');
  const [partialAmount, setPartialAmount] = useState('');
  
  useEffect(() => {
    loadData();
  }, [filter]);
  
  const loadData = async () => {
    try {
      // Load stats
      const statsResponse = await fetch(`${getApiBaseUrl()}/api/admin/refund-requests/stats`, {
        credentials: 'include'
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
      
      // Load requests based on filter
      const url = filter === 'all' 
        ? `${getApiBaseUrl()}/api/admin/refund-requests`
        : `${getApiBaseUrl()}/api/admin/refund-requests?status=${filter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Load refunds error:', error);
      toast.error('Failed to load refund requests');
    } finally {
      setLoading(false);
    }
  };
  
  const openDecisionDialog = (request, type) => {
    setSelectedRequest(request);
    setDecisionType(type);
    setAdminNotes('');
    setPartialAmount(request.amount_requested?.toString() || '');
    setShowDecisionDialog(true);
  };
  
  const handleDecision = async () => {
    if (!selectedRequest) return;
    
    setProcessingId(selectedRequest.refund_request_id);
    try {
      const body = {
        decision: decisionType,
        admin_notes: adminNotes || null
      };
      
      if (decisionType === 'approve' && partialAmount) {
        const partial = parseFloat(partialAmount);
        if (partial > 0 && partial !== selectedRequest.amount_requested) {
          body.partial_amount = partial;
        }
      }
      
      const response = await fetch(
        `${getApiBaseUrl()}/api/admin/refund-requests/${selectedRequest.refund_request_id}/decide`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowDecisionDialog(false);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process request');
      }
    } catch (error) {
      console.error('Decision error:', error);
      toast.error('Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  
  const formatSessionDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="admin-refunds-page">
      {/* Premium Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft size={24} />
            </Button>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Admin</p>
              <h1 className="text-2xl font-black uppercase tracking-tight">Refund Requests</h1>
            </div>
          </div>
        </div>
      </div>
      
      <div className="px-6 py-8 space-y-8 stagger-children">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            className={`stats-card cursor-pointer ${filter === 'pending' ? 'border-yellow-500' : ''}`}
            onClick={() => setFilter('pending')}
          >
            <div className="icon-container mb-4 text-yellow-500">
              <Clock size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-yellow-500 mb-1">{stats.pending_count}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Pending</p>
          </div>
          
          <div 
            className={`stats-card cursor-pointer ${filter === 'approved' ? 'border-green-500' : ''}`}
            onClick={() => setFilter('approved')}
          >
            <div className="icon-container mb-4 text-green-500">
              <CheckCircle size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-green-500 mb-1">{stats.approved_count}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Approved</p>
          </div>
          
          <div 
            className={`stats-card cursor-pointer ${filter === 'denied' ? 'border-red-500' : ''}`}
            onClick={() => setFilter('denied')}
          >
            <div className="icon-container mb-4 text-red-500">
              <XCircle size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-red-500 mb-1">{stats.denied_count}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Denied</p>
          </div>
          
          <div className="stats-card">
            <div className="icon-container mb-4 text-primary">
              <CurrencyDollar size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-primary mb-1">${stats.pending_amount?.toFixed(0) || 0}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Pending $</p>
          </div>
        </div>
        
        {/* Filter Tabs */}
        <div className="flex gap-2">
          {['pending', 'approved', 'denied', 'all'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'btn-primary' : ''}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
        
        {/* Requests List */}
        <div className="space-y-4">
          {requests.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="text-center py-12">
                <Wallet size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg mb-2">NO {filter.toUpperCase()} REQUESTS</h3>
                <p className="text-muted-foreground">
                  {filter === 'pending' 
                    ? 'All caught up! No pending refund requests.'
                    : `No ${filter} refund requests found.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            requests.map((request) => (
              <Card key={request.refund_request_id} className="bg-card border-border card-hover">
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    {/* Request Info */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-heading text-lg">{request.player_name}</h3>
                        <Badge className={
                          request.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                          request.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          'bg-red-500/20 text-red-500'
                        }>
                          {request.status.toUpperCase()}
                        </Badge>
                        <span className="text-2xl font-black text-primary">
                          ${request.amount_requested?.toFixed(2)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <User size={16} weight="duotone" />
                          <span>{request.parent?.name} ({request.parent?.email})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={16} weight="duotone" />
                          <span>Session: {formatSessionDate(request.session?.date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={16} weight="duotone" />
                          <span>{request.location_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={16} weight="duotone" />
                          <span>Requested: {formatDate(request.requested_at)}</span>
                        </div>
                      </div>
                      
                      {/* Hours before session indicator */}
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                        request.hours_before_session >= 24 
                          ? 'bg-green-500/10 text-green-500' 
                          : 'bg-yellow-500/10 text-yellow-500'
                      }`}>
                        <Warning size={16} weight="duotone" />
                        <span>
                          Cancelled {Math.abs(request.hours_before_session).toFixed(1)}h 
                          {request.hours_before_session >= 0 ? ' before' : ' after'} session
                        </span>
                      </div>
                      
                      {request.reason && (
                        <p className="text-sm text-muted-foreground italic">
                          "{request.reason}"
                        </p>
                      )}
                      
                      {request.admin_notes && (
                        <div className="p-3 bg-muted/30 rounded-lg text-sm">
                          <p className="font-medium mb-1">Admin Notes:</p>
                          <p className="text-muted-foreground">{request.admin_notes}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    {request.status === 'pending' && (
                      <div className="flex flex-row lg:flex-col gap-2 shrink-0">
                        <Button
                          onClick={() => openDecisionDialog(request, 'approve')}
                          className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                          disabled={processingId === request.refund_request_id}
                          data-testid={`approve-btn-${request.refund_request_id}`}
                        >
                          {processingId === request.refund_request_id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <>
                              <CheckCircle size={18} className="mr-2" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => openDecisionDialog(request, 'deny')}
                          variant="outline"
                          className="flex-1 text-red-500 border-red-500/30 hover:bg-red-500/10"
                          disabled={processingId === request.refund_request_id}
                          data-testid={`deny-btn-${request.refund_request_id}`}
                        >
                          <XCircle size={18} className="mr-2" />
                          Deny
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
      
      {/* Decision Dialog */}
      <Dialog open={showDecisionDialog} onOpenChange={setShowDecisionDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {decisionType === 'approve' ? 'APPROVE REFUND' : 'DENY REFUND'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="font-medium">{selectedRequest.player_name}</p>
                <p className="text-sm text-muted-foreground">
                  Parent: {selectedRequest.parent?.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  Amount: ${selectedRequest.amount_requested?.toFixed(2)}
                </p>
              </div>
              
              {decisionType === 'approve' && (
                <div>
                  <Label>Credit Amount</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-muted-foreground">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={selectedRequest.amount_requested}
                      value={partialAmount}
                      onChange={(e) => setPartialAmount(e.target.value)}
                      className="flex-1"
                      data-testid="partial-amount-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter full amount for 100% refund, or a partial amount
                  </p>
                </div>
              )}
              
              <div>
                <Label>Admin Notes (optional)</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={decisionType === 'approve' 
                    ? 'e.g., Approved due to medical emergency'
                    : 'e.g., Cancelled less than 24 hours before session'}
                  className="mt-1.5"
                  rows={3}
                  data-testid="admin-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDecisionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDecision}
              disabled={processingId !== null}
              className={decisionType === 'approve' 
                ? 'bg-green-500 hover:bg-green-600' 
                : 'bg-red-500 hover:bg-red-600'}
              data-testid="confirm-decision-btn"
            >
              {processingId ? (
                <LoadingSpinner size="sm" />
              ) : decisionType === 'approve' ? (
                <>Issue ${partialAmount || selectedRequest?.amount_requested?.toFixed(2)} Credit</>
              ) : (
                'Deny Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
